/**
 * /tm-invoices — T&M Invoice list with status filters.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button, Chip, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { tmInvoicesListQueryOptions, projectsListQueryOptions } from "@/lib/queries";
import type { TMInvoice, TMInvoiceStatus } from "@/lib/types";

export const Route = createFileRoute("/_auth/tm-invoices/")({
  component: TMInvoicesIndexPage,
});

type TabKey = "all" | TMInvoiceStatus;

const STATUS_CHIP: Record<TMInvoiceStatus, { label: string; color: "default" | "warning" | "success" | "error" | "info" }> = {
  draft:  { label: "Draft",  color: "default" },
  sent:   { label: "Sent",   color: "warning" },
  paid:   { label: "Paid",   color: "success" },
  voided: { label: "Voided", color: "error"   },
};

function TMInvoicesIndexPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("all");

  const invoicesQ = useQuery(tmInvoicesListQueryOptions());
  const projectsQ = useQuery(projectsListQueryOptions({ limit: 500 }));

  const projectsById = useMemo(() => {
    const m: Record<string, string> = {};
    (projectsQ.data?.items ?? []).forEach((p) => { m[p.id] = `${p.code} — ${p.name}`; });
    return m;
  }, [projectsQ.data?.items]);

  const allInvoices = invoicesQ.data?.items ?? [];

  const invoices = useMemo<TMInvoice[]>(() => {
    if (tab === "all") return allInvoices;
    return allInvoices.filter((i) => i.status === tab);
  }, [allInvoices, tab]);

  const tabCounts = useMemo(() => ({
    all:    allInvoices.length,
    draft:  allInvoices.filter((i) => i.status === "draft").length,
    sent:   allInvoices.filter((i) => i.status === "sent").length,
    paid:   allInvoices.filter((i) => i.status === "paid").length,
    voided: allInvoices.filter((i) => i.status === "voided").length,
  }), [allInvoices]);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              T&amp;M Invoices
            </h1>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {allInvoices.length} total
            </span>
          </div>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate({ to: "/tm-invoices/new" })}
          >
            New Invoice
          </Button>
        </div>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as TabKey)}
          sx={{ mt: 1, minHeight: 36 }}
        >
          {(["all", "draft", "sent", "paid", "voided"] as TabKey[]).map((key) => (
            <Tab
              key={key}
              value={key}
              label={`${key.charAt(0).toUpperCase() + key.slice(1)} (${tabCounts[key as keyof typeof tabCounts] ?? 0})`}
              sx={{ minHeight: 36, py: 0.5, textTransform: "capitalize" }}
            />
          ))}
        </Tabs>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {invoicesQ.isLoading ? (
          <div className="p-6 text-slate-500">Loading…</div>
        ) : invoices.length === 0 ? (
          <div className="p-6 text-slate-500">No invoices found.</div>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Labor</TableCell>
                <TableCell align="right">Materials</TableCell>
                <TableCell align="right">Equipment</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => {
                const chip = STATUS_CHIP[inv.status] ?? { label: inv.status, color: "default" };
                return (
                  <TableRow
                    key={inv.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate({ to: "/tm-invoices/$invoiceId", params: { invoiceId: inv.id } })}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>
                      {inv.invoice_number || inv.id.slice(0, 8).toUpperCase()}
                    </TableCell>
                    <TableCell>{projectsById[inv.project_id] ?? inv.project_id}</TableCell>
                    <TableCell>{inv.period_start} – {inv.period_end}</TableCell>
                    <TableCell align="right">${Number(inv.labor_total).toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="right">${Number(inv.materials_total).toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="right">${Number(inv.equipment_total).toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      ${Number(inv.grand_total).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <Chip label={chip.label} color={chip.color} size="small" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
